import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { Button } from '@totalsoft/rocket-ui';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { useToast } from 'hooks';
import { CardActionsContainer } from 'components/common/CardStyles';
import { exportModalOpenVar } from './variables';
import { useTranslation } from 'react-i18next';
import excelTableHeader from 'constants/excelTableHeader';
import { exportToExcel } from 'utils/excel';

const defaultMaxSize = 1048575;

const exportTypes = [
  {
    name: 'Excel',
    extension: 'xlsx'
  },
  {
    name: 'CSV',
    extension: 'csv'
  }
];

const ExportContainer = ({
  store,
  options: {
    queryOptions: { query, name, ...options },
    maxSize
  }
}) => {
  const apolloClient = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();
  const {
    store: { filters, pager }
  } = useListBrowserUtils(store);

  const [exportType, setExportType] = useState(null);
  const [exporting, setExporting] = useState(false);

  const variables = useMemo(() => {
    const value = { ...options?.variables };
    return { ...value, filters, pager: { ...pager, page: 1, size: maxSize || defaultMaxSize } };
  }, [options, filters, pager, maxSize]);

  const setOpen = useCallback(value => () => exportModalOpenVar(value), []);

  const handleExport = useCallback(async () => {
    if (!exportType) {
      addToast(t('ListBrowser.ExportTypeRequired'), 'info', { toastId: 'exportTypeRequiredToast' });
      return;
    }
    setExporting(true);
    const { data } = await apolloClient.query({ query, variables, fetchPolicy: 'network-only', ...options });
    let exportValues = [];
    if (data) {
      const exportDetails = data[name];
      if (Array.isArray(exportDetails)) exportValues = exportDetails;
      else exportValues = exportDetails.values;
    }
    setExporting(false);
    const { formattedList, fileName, sheetName } = excelTableHeader(exportValues, t, name, exportType.extension);
    exportToExcel(formattedList, fileName, sheetName, exportType.extension);
    addToast(t('ListBrowser.Exported'), 'success', { toastId: 'exportedMessageToast' });
    exportModalOpenVar(false);
  }, [addToast, apolloClient, exportType, name, options, query, t, variables]);

  const handleTypeChange = useCallback(type => () => setExportType(type), []);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {exportTypes.map(type => (
          <FormControlLabel
            key={type.name}
            control={<Checkbox checked={exportType?.name === type.name} disabled={exporting} onChange={handleTypeChange(type)} />}
            label={type.name}
          ></FormControlLabel>
        ))}
      </Box>
      <CardActionsContainer sx={{ justifyContent: 'end' }}>
        <Button onClick={handleExport} disabled={exporting}>
          {t('ListBrowser.Export')}
        </Button>
        <Button onClick={setOpen(false)}>{t('General.Close')}</Button>
      </CardActionsContainer>
    </>
  );
};

ExportContainer.propTypes = {
  store: PropTypes.object.isRequired,
  options: PropTypes.object
};

export default ExportContainer;
